import clsx from 'clsx';
import React, { ReactNode } from 'react';
import SpinnerIcon from '../../assets/icons/spinner-icon.svg?react';
import { Link } from 'react-router';

interface ButtonProps {
  otherClasses?: string;
  children: string | ReactNode;
  href?: string;
  type?: 'button' | 'reset' | 'submit';
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'stripe' | 'link';
  disable?: boolean;
  loading?: boolean;
  size?: 'bigger' | 'big' | 'small' | 'smaller';
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
}

const Button: React.FC<ButtonProps> = ({
  otherClasses,
  children,
  href,
  variant = 'primary',
  onClick,
  type = 'button',
  disable,
  loading,
  size,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
}) => {
  const buttonClasses = clsx(
    otherClasses,
    'global-transition',
    variant === 'primary' &&
      `flex items-center justify-center gap-2.5 bg-brand-byzantine shadow-button-box-shadow rounded-lg cursor-pointer text-light-neutral-tones-background font-inter-variable font-bold -tracking-button xl:p-25 xl:text-button lg:py-15 lg:px-25 lg:text-xl/6 md:py-15 md:px-2.5 md:text-lg text-base p-2.5 border-2 border-transparent hover:bg-white hover:border-brand-byzantine hover:text-brand-byzantine disabled:bg-additional-disable disabled:text-neutral-tones-text/50 disabled:hover:border-transparent disabled:cursor-not-allowed disabled:hover:bg-additional-disable `,
    variant === 'secondary' &&
      `flex items-center justify-center gap-2.5 bg-light-neutral-tones-background shadow-button-box-shadow rounded-lg cursor-pointer text-brand-byzantine font-inter-variable font-bold -tracking-button xl:p-25 xl:text-button lg:py-15 lg:px-25 lg:text-xl/6 md:py-15 md:px-2.5 md:text-lg text-base p-2.5 border-2 border-neutral-tones-text/10 hover:bg-white hover:border-brand-byzantine hover:text-brand-byzantine disabled:bg-additional-disable disabled:text-neutral-tones-text/50 disabled:border-transparent disabled:hover:border-transparent disabled:cursor-not-allowed disabled:hover:bg-additional-disable `,
    variant === 'tertiary' &&
      `flex items-center justify-center gap-2.5 border border-neutral-tones-text/50 lg:px-25 lg:py-15 md:px-2.5 md:py-15 p-2.5 rounded-md font-inter-variable font-medium text-neutral-tones-text/50 lg:text-xl md:text-lg text-base hover:border-neutral-tones-text hover:text-neutral-tones-text hover:font-bold hover:border-2`,
    variant === 'stripe' &&
      `p-2.5 text-base md:px-25 md:py-15 rounded-lg md:text-xl cursor-pointer font-inter-variable font-bold md:font-medium text-stripe-brand bg-white shadow-button-box-shadow flex items-center gap-1 justify-center`,
    variant === 'link' &&
      `flex justify-between gap-1 items-center text-base lg:text-lg font-normal font-inter-variable -tracking-text-area leading-5 text-neutral-tones-text cursor-pointer hover:text-brand-byzantine`,
    size === 'bigger'
      ? '!p-25 !text-button'
      : size === 'big'
        ? '!py-[13px] !px-25 !text-xl/6'
        : size === 'small'
          ? '!py-3 !px-2.5 !text-lg/5'
          : size === 'smaller' && '!text-base/5 !py-2 !px-2.5',
  );

  const commonProps = {
    className: buttonClasses,
    'data-testid': 'button',
    onClick: (e: React.MouseEvent<HTMLElement>) => onClick && onClick(e),
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
  };

  const renderContent = () =>
    loading ? (
      <>
        <SpinnerIcon
          className="animate-spin [&>rect]:fill-current"
          aria-hidden="true"
        />
        <span className="-mb-1" aria-hidden={!!ariaLabel}>
          Loading...
        </span>
      </>
    ) : (
      children
    );

  return href ? (
    <Link to={href} {...commonProps}>
      {renderContent()}
    </Link>
  ) : (
    <button {...commonProps} type={type} disabled={disable}>
      {renderContent()}
    </button>
  );
};

export default Button;
